import React from 'react';
import PropTypes from 'prop-types';
import MuiTextField from '@material-ui/core/TextField';
import T from 'i18n-react';
import { PRESENT_REGEX } from '../variables';
import { withStyles } from '@material-ui/core/styles';

const debounce = require('lodash/debounce');

const HelperText = withStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-end',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(0.5),
    },
  },
  count: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
}))(({ classes, text, count, maxLength }) => (
  <span className={classes.root}>
    <span className="text-break-all">{text}</span>
    {maxLength > 0 && (
      <span className={classes.count}>{`${
        typeof count === 'number' ? count : 0
      } / ${maxLength}`}</span>
    )}
  </span>
));

class TextField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      value: props.value != null ? props.value : '',
      error: props.error,
      helperText: props.helperText,
      composing: false,
      changed: false,
    };

    this._denouncedSave = this.denouncedSave();
    this._mounted = false;
    this._touched = false;

    this._error = false;

    this.fieldRef = React.createRef();

    this.denouncedSave = this.denouncedSave.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleCompositionStart = this.handleCompositionStart.bind(this);
    this.handleCompositionEnd = this.handleCompositionEnd.bind(this);
    this.validate = this.validate.bind(this);
    this.reset = this.reset.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this._error = this.isInvalid();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.required !== this.props.required && this._touched) {
      this.validate();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  denouncedSave() {
    const { waitingTime, autoSave } = this.props;

    return debounce(() => {
      if (!this._mounted) return;

      const { error, composing, changed } = this.state;

      if (!composing && changed && autoSave && !error) {
        const field = this.fieldRef.current;
        autoSave(field.value);
        this.setState({ changed: false });
      }
    }, waitingTime);
  }

  sanitizeValue = (value) =>
    value != null && this.props.disableEnter
      ? value.replace(/\r?\n/g, '')
      : value;

  handleChange(event) {
    this._touched = true;
    const value = this.sanitizeValue(event.target.value);
    const isValid = this.validate({ value, changed: true });

    // Create new object to set a sanitized value
    const protoEvent = Object.create(event);
    protoEvent.value = value;
    this.props.onChange?.(protoEvent, isValid);
  }

  handleFocus() {
    this.fieldRef.current?.focus();
  }

  handleBlur(event) {
    const isValid = this.validate();
    this.props.onBlur?.(event, isValid);
  }

  handleKeyDown(event) {
    const { disableEnter, onKeyDown, autoSave } = this.props;
    onKeyDown?.(event);
    event.key === 'Enter' && disableEnter && event.preventDefault();

    autoSave && this._denouncedSave();
  }

  handleCompositionStart() {
    this.setState({ composing: true });
  }

  handleCompositionEnd() {
    this.setState({ composing: false });
  }

  getErrorMessage(field) {
    const validity = field.validity;

    if (validity.tooLong) {
      return T.translate('form.tooLong', { length: field.maxLength });
    } else if (validity.tooShort) {
      return T.translate('form.tooShort', { length: field.minLength });
    } else if (validity.rangeOverflow) {
      return T.translate('form.lessThanOrEqualTo', { max: field.max });
    } else if (validity.rangeUnderflow) {
      return T.translate('form.greaterThanOrEqualTo', { min: field.min });
    } else if (
      validity.badInput ||
      validity.patternMismatch ||
      !field.required ||
      this.sanitizeValue(field.value)
    ) {
      return T.translate('form.invalid');
    } else {
      return T.translate('form.required');
    }
  }

  isInvalid = () => {
    const { inputProps, required } = this.props;
    const pattern = inputProps?.pattern;
    const field = this.fieldRef.current;
    if (!field) return false;

    const value = this.sanitizeValue(field.value);
    if (
      field.type.toLowerCase() === 'number' &&
      !field.required &&
      value === ''
    ) {
      // Allow empty value when input type is number and  not required
      this._error = false;
    } else if (field.tagName === 'TEXTAREA') {
      // text area does not support pattern field
      const textPattern = pattern ? `^${pattern}$` : PRESENT_REGEX;
      this._error = !(
        field.checkValidity() &&
        ((!required && value === '') ||
          new RegExp(textPattern).test(value || ''))
      );
    } else {
      this._error =
        (!pattern && !new RegExp(PRESENT_REGEX).test(value || '')) ||
        !field.checkValidity();
    }
    return this._error;
  };

  getHelperText = (invalid) => {
    const field = this.fieldRef.current;
    if (!field) return '';

    if (invalid) {
      return this.getErrorMessage(field);
    }
    return this.props.helperText || '';
  };

  validate(state = {}) {
    const isInvalid = this.isInvalid();
    const helperText = this.getHelperText(isInvalid);
    this.setState({ error: isInvalid, helperText, ...state });
    return !isInvalid;
  }

  reset({
    value = this.props.value,
    error = false,
    helperText = this.props.helperText,
  } = {}) {
    this.setState({
      value,
      error,
      helperText,
      composing: false,
      changed: false,
    });
  }

  render() {
    const {
      disableEnter,
      disableHelperText,
      disableControl,
      countWord,
      waitingTime,
      autoSave,
      value: propsValue,
      InputProps,
      onChange,
      onKeyDown,
      onBlur,
      error: errorProps,
      ...fieldProps
    } = this.props;

    const { value, error } = this.state;
    const { inputProps } = fieldProps;
    const maxLength = inputProps?.maxLength;

    if (this._mounted && this.fieldRef.current && disableControl) {
      this.fieldRef.current.value = propsValue?.toString() || '';
      this.isInvalid();
    }

    const invalid = this._error;
    const helperText =
      this._mounted && disableControl
        ? this.getHelperText(invalid)
        : this.state.helperText;
    const hideHelperText =
      disableHelperText || (!helperText && maxLength == null && !countWord);

    return (
      <MuiTextField
        {...fieldProps}
        value={disableControl ? (propsValue != null ? propsValue : '') : value}
        error={disableControl ? invalid : error}
        inputRef={this.fieldRef}
        {...(!InputProps?.readOnly && {
          error: disableControl ? invalid : error,
          onKeyDown: this.handleKeyDown,
          onChange: this.handleChange,
          onBlur: this.handleBlur,
        })}
        InputProps={{
          ...InputProps,
          onCompositionStart: this.handleCompositionStart,
          onCompositionEnd: this.handleCompositionEnd,
        }}
        {...(!hideHelperText &&
          !InputProps?.readOnly && {
            helperText: (
              <HelperText
                text={helperText}
                maxLength={maxLength}
                count={
                  (typeof value === 'number'
                    ? value.toString().length
                    : value?.length) || 0
                }
              />
            ),
          })}
      />
    );
  }
}

TextField.defaultProps = {
  waitingTime: 1000,
};

TextField.propTypes = {
  disableEnter: PropTypes.bool,
  disableHelperText: PropTypes.bool, // Not display any helper text including error message
  countWord: PropTypes.bool,
  waitingTime: PropTypes.number,
  autoSave: PropTypes.func,
  autoUpdate: PropTypes.bool,
  disableControl: PropTypes.bool, // Control value by props, not state
};

export default TextField;
